'use client'

import { AlertOctagon, Megaphone, Sparkles, Wrench, type LucideIcon } from 'lucide-react'
import { NotificationSeverity } from '@/shared/schema/system-notification'

interface NotificationStyle {
  icon: LucideIcon
  /** Severity-tinted rounded tile behind the icon */
  iconTile: string
  /** Soft severity glow under the icon tile (modal header) */
  tileGlow: string
  /** Subtle severity gradient wash for the modal header */
  headerWash: string
  /** Solid severity dot used inside the metadata pill */
  dot: string
  /** Severity-coloured icon (static, no hover shade) */
  accentIcon: string
  /** Front-card surface shadow: severity glow + neutral 1px ring (no hard border) */
  cardShadow: string
  /** Softer shadow for cards sitting behind the front of the stack */
  stackedShadow: string
  /** Severity-coloured action text ("View details") */
  accentText: string
}

/**
 * Notification styling — severity is carried by the icon colour and a soft
 * glow shadow on a translucent white surface (the design system's overlay
 * language, matching Sonner toasts), never by a solid colour fill.
 *
 * Severity mappings per docs/BRAND-COLOUR-GUIDELINES.md:
 * - announcement: blue (special announcements)
 * - feature: emerald (new features, additions)
 * - maintenance: amber (warnings, attention needed)
 * - critical: red (critical alerts, errors)
 */
export function getNotificationStyle(severity: NotificationSeverity): NotificationStyle {
  const styles: Record<NotificationSeverity, NotificationStyle> = {
    announcement: {
      icon: Megaphone,
      iconTile: 'bg-blue-500/10 text-blue-600',
      tileGlow: 'shadow-[0_2px_10px_rgba(59,130,246,0.25)]',
      headerWash: 'bg-linear-to-b from-blue-500/[0.07] to-transparent',
      dot: 'bg-blue-500',
      accentIcon: 'text-blue-600',
      cardShadow:
        'shadow-[0_8px_24px_rgba(59,130,246,0.14),0_2px_8px_rgba(59,130,246,0.08),0_0_0_1px_rgba(15,23,42,0.06)]',
      stackedShadow: 'shadow-[0_4px_12px_rgba(59,130,246,0.10),0_0_0_1px_rgba(15,23,42,0.05)]',
      accentText: 'text-blue-600 hover:text-blue-700'
    },
    feature: {
      icon: Sparkles,
      iconTile: 'bg-emerald-500/10 text-emerald-600',
      tileGlow: 'shadow-[0_2px_10px_rgba(16,185,129,0.25)]',
      headerWash: 'bg-linear-to-b from-emerald-500/[0.07] to-transparent',
      dot: 'bg-emerald-500',
      accentIcon: 'text-emerald-600',
      cardShadow:
        'shadow-[0_8px_24px_rgba(16,185,129,0.14),0_2px_8px_rgba(16,185,129,0.08),0_0_0_1px_rgba(15,23,42,0.06)]',
      stackedShadow: 'shadow-[0_4px_12px_rgba(16,185,129,0.10),0_0_0_1px_rgba(15,23,42,0.05)]',
      accentText: 'text-emerald-600 hover:text-emerald-700'
    },
    maintenance: {
      icon: Wrench,
      iconTile: 'bg-amber-500/10 text-amber-600',
      tileGlow: 'shadow-[0_2px_10px_rgba(245,158,11,0.28)]',
      headerWash: 'bg-linear-to-b from-amber-500/8 to-transparent',
      dot: 'bg-amber-500',
      accentIcon: 'text-amber-600',
      cardShadow:
        'shadow-[0_8px_24px_rgba(245,158,11,0.16),0_2px_8px_rgba(245,158,11,0.08),0_0_0_1px_rgba(15,23,42,0.06)]',
      stackedShadow: 'shadow-[0_4px_12px_rgba(245,158,11,0.10),0_0_0_1px_rgba(15,23,42,0.05)]',
      accentText: 'text-amber-600 hover:text-amber-700'
    },
    critical: {
      icon: AlertOctagon,
      iconTile: 'bg-red-500/10 text-red-600',
      tileGlow: 'shadow-[0_2px_10px_rgba(239,68,68,0.30)]',
      headerWash: 'bg-linear-to-b from-red-500/8 to-transparent',
      dot: 'bg-red-500',
      accentIcon: 'text-red-600',
      cardShadow:
        'shadow-[0_8px_24px_rgba(239,68,68,0.18),0_2px_8px_rgba(239,68,68,0.10),0_0_0_1px_rgba(239,68,68,0.10)]',
      stackedShadow: 'shadow-[0_4px_12px_rgba(239,68,68,0.12),0_0_0_1px_rgba(15,23,42,0.05)]',
      accentText: 'text-red-600 hover:text-red-700'
    }
  }

  return styles[severity] || styles.announcement
}
